import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'https://ventro.energy';

const CompanyPartnershipsModal = ({ onClose }) => {
  const [partnerships, setPartnerships] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState('');

  useEffect(() => {
    fetchPartnerships();
    fetchAvailablePartners();
  }, []);

  const fetchPartnerships = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/accessible_companies/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setPartnerships(response.data || []);
    } catch (error) {
      console.error('Error fetching partnerships:', error);
      setPartnerships([]);
    }
  };

  const fetchAvailablePartners = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/warehouse/company-access/available_partners/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      setAvailablePartners(response.data || []);
    } catch (error) {
      console.error('Error fetching available partners:', error);
      setAvailablePartners([]);
    } finally {
      setLoading(false);
    }
  };

  const createPartnership = async () => {
    if (!selectedPartner) return;
    
    setCreating(true);
    try {
      await axios.post(`${API_BASE_URL}/api/warehouse/company-access/create_partnership/`, {
        partner_company_id: selectedPartner,
        access_level: 'full_access'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        }
      });

      alert('Partnership created successfully!');
      setSelectedPartner('');
      fetchPartnerships();
      fetchAvailablePartners();
    } catch (error) {
      console.error('Error creating partnership:', error);
      const errorMessage = error.response?.data?.error || 'Error creating partnership';
      alert(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const removePartnership = async (partnerId, partnerName) => {
    if (!window.confirm(`Remove partnership with ${partnerName}?`)) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/warehouse/company-access/remove_partnership/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        },
        data: {
          partner_company_id: partnerId
        }
      });

      alert('Partnership removed successfully!');
      fetchPartnerships();
      fetchAvailablePartners();
    } catch (error) {
      console.error('Error removing partnership:', error);
      alert('Error removing partnership');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-3/4 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Company Partnerships
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              Manage partnerships with other companies to enable warehouse inventory sharing.
              Only partnered companies can share inventory items with each other.
            </p>

            {/* Current Partnerships */}
            <div className="mb-6">
              <h4 className="text-md font-medium text-gray-800 mb-3">Current Partnerships</h4>
              {loading ? (
                <div className="text-center text-gray-500">Loading partnerships...</div>
              ) : partnerships.length === 0 ? (
                <div className="text-center text-gray-500 py-4 border-2 border-dashed border-gray-300 rounded-lg">
                  No partnerships established yet
                </div>
              ) : (
                <div className="space-y-3">
                  {partnerships.map(partnership => (
                    <div key={partnership.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                      <div>
                        <span className="font-medium text-gray-900">{partnership.name}</span>
                        <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                          {partnership.access_level?.replace('_', ' ') || 'Full Access'}
                        </span>
                      </div>
                      <button
                        onClick={() => removePartnership(partnership.id, partnership.name)}
                        className="text-red-600 hover:text-red-900 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New Partnership */}
            <div>
              <h4 className="text-md font-medium text-gray-800 mb-3">Add New Partnership</h4>
              <div className="flex items-center space-x-3">
                <select
                  value={selectedPartner}
                  onChange={(e) => setSelectedPartner(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={creating}
                >
                  <option value="">Select a company...</option>
                  {availablePartners.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={createPartnership}
                  disabled={!selectedPartner || creating}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {creating ? 'Creating...' : 'Add Partnership'}
                </button>
              </div>
              {availablePartners.length === 0 && !loading && (
                <p className="text-sm text-gray-500 mt-2">
                  All available companies are already partnered with you.
                </p>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-2">How Partnerships Work:</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Partnerships are bidirectional - both companies can share inventory with each other</li>
              <li>• Only partnered companies will appear in the sharing options when managing inventory items</li>
              <li>• Removing a partnership will stop all inventory sharing between the companies</li>
              <li>• All partnerships provide full access (view, issue, return, create) by default</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyPartnershipsModal;